<template>
  <div>
    <p class="mt-5">
      {{ t('deceasedDetails.fieldsMarkedWithStar') }}
    </p>
    <!-- Namn -->
    <option-label class="mt-8 mb-0" :title="t('deceasedDetails.form.name.labelDeceased') + '*'" />
    <v-text-field
      v-model="donationCase.caseInfo.name"
      data-cy="caseview-name-field"
      :placeholder="t('deceasedDetails.form.name.inputPlaceholder')"
      :hint="t('deceasedDetails.form.name.hint')"
      :persistent-hint="true"
      required
      :rules="[rules.requiredField(t('deceasedDetails.form.name.label'))]"
    />
    <!-- Ort  -->
    <option-label class="mb-0" :title="t('deceasedDetails.form.funeralPlace.label') + '*'" />
    <v-text-field
      v-model="donationCase.caseInfo.events[0].place"
      data-cy="caseview-city-field"
      :placeholder="t('deceasedDetails.form.funeralPlace.placeholder')"
      :hint="t('deceasedDetails.form.funeralPlace.hint')"
      required
      :rules="[rules.requiredField(t('deceasedDetails.form.funeralPlace.label'))]"
    />
    <!-- Födelsedatum -->
    <v-row no-gutters>
      <v-col cols="12" sm="6" :class="{ 'pr-sm-3': true }">
        <option-label class="mb-0" :title="t('deceasedDetails.form.birthDay.label')" />
        <date-field-component
          v-model="donationCase.caseInfo.events[1].date"
          data-cy="caseview-birth-field"
          :placeholder="t('deceasedDetails.form.birthDay.placeholder')"
          :hint="t('deceasedDetails.form.birthDay.hint')"
          :max-date="today"
        />
      </v-col>
      <v-col cols="12" sm="6" :class="{ 'pl-sm-3': true }">
        <option-label class="mb-0" :title="t('deceasedDetails.form.deathDay.label')" />
        <date-field-component
          v-model="donationCase.caseInfo.events[2].date"
          data-cy="caseview-death-field"
          :placeholder="t('deceasedDetails.form.deathDay.placeholder')"
          :hint="t('deceasedDetails.form.deathDay.hint')"
          :max-date="today"
          :rules="[birthDateBeforeDeathRule]"
          :error="!!birthError"
          :error-messages="birthError ? [birthError] : []"
        />
      </v-col>
    </v-row>
    <!-- Begravningsbyrå -->
    <option-label class="mb-0" :title="t('deceasedDetails.form.recipient.label')" />
    <v-autocomplete
      v-model="donationCase.recipient"
      v-model:search="query"
      data-cy="caseview-recipient-autocomplete"
      :items="recipients"
      :loading="recipientLoading"
      return-object
      hide-no-data
      item-title="name"
      class="placeholder-indent"
      :placeholder="t('deceasedDetails.form.recipient.inputPlaceholder')"
      density="comfortable"
      :hint="t('deceasedDetails.form.recipient.hint')"
      append-outer-icon="mdi-database-search"
      clearable
    />
    <!-- Begravningsdatum -->
    <!--
    <date-field-component
      v-model="donationCase.caseInfo.events[0].date"
      data-cy="caseview-funeraldate-field"
      :label="t('deceasedDetails.form.funeralDate.label')"
      :placeholder="t('deceasedDetails.form.funeralDate.placeholder')"
      :hint="t('deceasedDetails.form.funeralDate.hint')"
      :min-date="donationCase.caseInfo.events[2].date"
      :rules="[funeralDateAfterDeathRule]"
    />
    -->
    <!-- Begravningstid -->
    <!--
    <time-field-component
      v-model="donationCase.caseInfo.events[0].time"
      data-cy="caseview-funeraltime-field"
      :label="t('deceasedDetails.form.funeralTime.label')"
      :placeholder="t('deceasedDetails.form.funeralTime.placeholder')"
      :hint="t('deceasedDetails.form.funeralTime.hint')"
      :required="false"
    />
    -->
    <!-- Kyrka -->
    <option-label class="mb-0" :title="t('deceasedDetails.form.funeralVenue.label')" />
    <v-text-field
      v-model="donationCase.caseInfo.events[0].venue"
      data-cy="caseview-church-field"
      :placeholder="t('deceasedDetails.form.funeralVenue.placeholder')"
      :hint="t('deceasedDetails.form.funeralVenue.hint')"
    />
    <!-- Övrigt -->
    <option-label class="mb-0" :title="t('deceasedDetails.form.additionalInfo.label')" />
    <v-textarea
      v-model="donationCase.caseInfo.additionalInfo"
      data-cy="caseview-other-area"
      :placeholder="t('deceasedDetails.form.additionalInfo.placeholder')"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { DonationCase } from '@/interfaces/donation/donationcase/donationCase';
import dayjs from 'dayjs';
import { Recipient } from '@/interfaces/donation/certificate/recipient';
import DateFieldComponent from '@/components/form/DateFieldComponent.vue';
import TimeFieldComponent from '@/components/form/TimeFieldComponent.vue';
import OptionLabel from '@/components/OptionLabel.vue';
import { ENDPOINTS, get } from '@/api/mainApi';
import { useLabel } from '@/composables/useLabel';
import { useValidationRules } from '@/composables/useValidationRules';
import store from '@/store';

export default defineComponent({
  components: {
    DateFieldComponent,
    TimeFieldComponent,
    OptionLabel,
  },
  props: {
    value: {
      type: Object as () => DonationCase,
      default: () => ({}) as DonationCase,
    },
  },
  emits: ['update:value'],
  setup(props, { emit }) {
    const { t } = useLabel();
    const { rules: baseRules } = useValidationRules(t);
    const rules = {
      requiredField: baseRules.value.requiredField,
    };

    const query = ref('');
    const timerId = ref(0);
    const recipientLoading = ref(false);
    const recipients = ref([] as Recipient[]);

    const donationCase = computed({
      get: () => props.value as DonationCase,
      set: (val: DonationCase) => emit('update:value', val),
    });

    const today = computed(() => dayjs().format('YYYY-MM-DD'));

    const getRecipients = async (query: string) => {
      clearTimeout(timerId.value);
      const validCountries = ['SWEDEN', 'NORWAY', 'GERMANY'];
      const userLocation = store.state.userLocation;
      const countryName =
        typeof userLocation === 'string' && validCountries.includes(userLocation)
          ? userLocation
          : 'SWEDEN';
      timerId.value = setTimeout(async () => {
        recipientLoading.value = true;
        recipients.value = await get(ENDPOINTS.RECIPIENTS + '/' + ENDPOINTS.SEARCH + '/' + query, {
          funeralhome: true,
          country: countryName,
        });
        recipientLoading.value = false;
      }, 250);
    };

    const birthDateBeforeDeathRule = () => {
      const birthDate = donationCase.value.caseInfo.events[1].date;
      const deathDate = donationCase.value.caseInfo.events[2].date;

      if (!birthDate || !deathDate) {
        return true;
      }

      return new Date(birthDate) <= new Date(deathDate)
        ? true
        : t('deceasedDetails.form.deathAfterBirth');
    };

    const birthError = computed(() => {
      const birthDate = donationCase.value.caseInfo.events[1].date;
      const deathDate = donationCase.value.caseInfo.events[2].date;
      if (!birthDate || !deathDate) return '';
      return new Date(birthDate) > new Date(deathDate)
        ? t('deceasedDetails.form.deathAfterBirth')
        : '';
    });

    const funeralDateAfterDeathRule = () => {
      const deathDate = donationCase.value.caseInfo.events[2].date;
      const funeralDate = donationCase.value.caseInfo.events[0].date;

      if (!deathDate || !funeralDate) {
        return true;
      }

      return new Date(funeralDate) >= new Date(deathDate)
        ? true
        : t('deceasedDetails.form.funeralAfterDeath');
    };

    watch(query, (val) => {
      if (val && val.length > 0) {
        getRecipients(val);
      }
    });

    watch(
      () => donationCase.value.recipient,
      (newRecipient) => {
        if (newRecipient && !recipients.value.find((r) => r.id === newRecipient.id)) {
          recipients.value.unshift(newRecipient);
        }
      },
      { immediate: true }
    );

    return {
      query,
      recipientLoading,
      recipients,
      donationCase,
      rules,
      t,
      birthDateBeforeDeathRule,
      birthError,
      funeralDateAfterDeathRule,
      today,
    };
  },
});
</script>
